import currency from "currency.js";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalFilter, useSortBy, useTable } from "react-table";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { initialMarginUtilization, maintenanceMarginUtilization, utilizationColor } from "../../utils/math";
import { shortenAddress } from "../../utils/strings";
import ConfirmationModal from "../shared/ConfirmationModal";
import RowActionButton from "../shared/RowActionButton";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { Align, CopyButton, DashboardRow, TableHeaderCell, TableWrapper } from "../Table/style";
import { ITableColumn } from "../Table/TableColumn";
import {
  AccountButton,
  ActionWrapper,
  GroupedCells,
  LinkText,
  PaginationButton,
  QuickAddressTag,
  MobileQuickAddressWrapper,
  MobilePaginationCol,
  MobileFooterRow,
} from "./style";
import { AccountTypeEnum, IAccount } from "../../interfaces/aevo";
import { DataContext } from "../../context/DataContext";
import { useAccount } from "../../hooks/api/admin/useAccount";
import { Spinner } from "../shared/Spinner";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { AccountFilter } from "../Table/AccountFilter";
import arrow from "../../assets/svg/arrow-up-right.svg";
import { quickAddresses } from ".";
import { COMPONENTS } from "../../constants/design/spacing";
import { MobileDirectory } from "../shared/MobileDirectory";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { FONT_SIZE } from "../../constants/design/fontSize";

export function MobileDashboard() {
  const { accounts, records, pagination, setPagination, size, totalAccounts, isLoading } = useContext(DataContext);
  const [show, setShow] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<IAccount>();
  const { updateAccount } = useAccount();
  const { setAddress, clearFilter } = useContext(DataContext);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const dashboardFooterRef = useRef<HTMLDivElement>(null);

  // When landing on the dashboard, the selected address from URL param should be cleared to disable Deribit queries
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setAddress("");
    clearFilter();
  }, [setAddress, clearFilter]);

  const memoizedAccounts = useMemo(() => accounts || [], [accounts]);

  const lastPage = useMemo(() => Math.ceil(totalAccounts / size), [totalAccounts, size]);

  const onPrevPage = useCallback(() => {
    if (pagination > 1) {
      setPagination(pagination - 1);
    }
  }, [pagination, setPagination]);

  const onNextPage = useCallback(() => {
    if (pagination < lastPage) {
      setPagination(pagination + 1);
    }
  }, [lastPage, pagination, setPagination]);

  // Calculate the start and end indices for the current page
  const startIndex = useMemo(() => (pagination - 1) * size + 1, [pagination, size]);

  const endIndex = useMemo(() => pagination * size, [pagination, size]);

  // Determine the format for displaying the range
  const displayRange = useMemo(
    () =>
      `${currency(startIndex, { precision: 0 }).format({ symbol: "" })} - ${currency(
        Math.min(endIndex, totalAccounts),
        { precision: 0 }
      ).format({ symbol: "" })}`,
    [endIndex, startIndex, totalAccounts]
  );

  const onCopy = useCallback(
    (address: string) => {
      navigator.clipboard.writeText(address);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Address Copied",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  const columns: ITableColumn<IAccount>[] = useMemo(
    () => [
      {
        id: "address",
        title: "Account Address",
        align: "left",
        accessor: "account",
        Cell: ({ row }: any) => (
          <div style={{ display: "flex" }}>
            <CopyButton
              onClick={(e) => {
                e.stopPropagation();
                onCopy(row.original.account);
              }}
            >
              <Copy />
            </CopyButton>
            <LinkText
              style={{
                color: records[row.original.account] ? COLORS.blue.one : TEXT_COLORS.one,
              }}
            >
              <div>{records[row.original.account] ? records[row.original.account] : row.original.account}</div>
              <div style={{ color: records[row.original.account] ? COLORS.highlight.two : COLORS.neutral.three }}>
                {row.original.username}
              </div>
            </LinkText>
          </div>
        ),
      },
      {
        title: "Equity",
        align: "right",
        accessor: "equity",
        valueExtractor: (value) => currency(value).format(),
      },
      {
        title: "IM%",
        align: "right",
        id: "IM%",
        Cell: ({ row }: any) => {
          const percentage = initialMarginUtilization(
            Number(row.original.equity),
            Number(row.original.initial_margin),
            Number(row.original.maintenance_margin)
          ).toFixed(1);
          return (
            <Align align="right" style={{ color: utilizationColor(Number(percentage)) }}>
              {percentage}%
            </Align>
          );
        },
        canSort: true,
        sortType: (a, b) => {
          const percentageA = initialMarginUtilization(
            Number(a.original.equity),
            Number(a.original.initial_margin),
            Number(a.original.maintenance_margin)
          );

          const percentageB = initialMarginUtilization(
            Number(b.original.equity),
            Number(b.original.initial_margin),
            Number(b.original.maintenance_margin)
          );

          if (percentageA > percentageB) return 1;
          if (percentageA < percentageB) return -1;
          return 0;
        },
      },
      {
        title: "IM",
        align: "right",
        accessor: "initial_margin",
        Cell: ({ row, value }: any) => {
          const percentage = initialMarginUtilization(
            Number(row.original.equity),
            Number(row.original.initial_margin),
            Number(row.original.maintenance_margin)
          ).toFixed(1);
          return (
            <Align
              align="right"
              style={{
                color: value < 0.01 ? TEXT_COLORS.three : utilizationColor(Number(percentage)),
              }}
            >
              {currency(value).format()}
            </Align>
          );
        },
      },
      {
        title: "MM%",
        align: "right",
        id: "MM%",
        Cell: ({ row }: any) => {
          const percentage = maintenanceMarginUtilization(
            Number(row.original.equity),
            Number(row.original.maintenance_margin)
          ).toFixed(1);
          return (
            <Align align="right" style={{ color: utilizationColor(Number(percentage)) }}>
              {percentage}%
            </Align>
          );
        },
        canSort: true,
        sortType: (a, b) => {
          const percentageA = maintenanceMarginUtilization(
            Number(a.original.equity),
            Number(a.original.maintenance_margin)
          );

          const percentageB = maintenanceMarginUtilization(
            Number(b.original.equity),
            Number(b.original.maintenance_margin)
          );

          if (percentageA > percentageB) return 1;
          if (percentageA < percentageB) return -1;
          return 0;
        },
      },
      {
        title: "MM",
        align: "right",
        accessor: "maintenance_margin",
        Cell: ({ row, value }: any) => {
          const percentage = maintenanceMarginUtilization(
            Number(row.original.equity),
            Number(row.original.maintenance_margin)
          ).toFixed(1);
          return (
            <Align
              align="right"
              style={{
                color: value < 0.01 ? TEXT_COLORS.three : utilizationColor(Number(percentage)),
              }}
            >
              {currency(value).format()}
            </Align>
          );
        },
      },
      {
        id: "rowActionsSideTypExpiry",
        title: "Actions",
        align: "center",
        Cell: ({ row }: any) => (
          <Align align="center">
            <GroupedCells>
              <ActionWrapper>
                <RowActionButton
                  variant={"edit"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAccount(row.original as IAccount);
                    setShow(true);
                  }}
                />
              </ActionWrapper>
            </GroupedCells>
          </Align>
        ),
      },
    ],
    [onCopy, records]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: memoizedAccounts,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
      autoResetSortBy: false,
    },
    useGlobalFilter,
    useSortBy
  );

  return (
    <>
      <ConfirmationModal
        icon={<EditIcon />}
        primaryColor={COLORS.blue.one}
        secondaryColor={COLORS.blue.two}
        tertiaryColor={COLORS.blue.three}
        title={"Update Account Type"}
        subtitle={shortenAddress(selectedAccount?.account || "")}
        show={show}
        minWidth={"250px"}
        onHide={() => setShow(false)}
        extras={
          <>
            <AccountButton
              isActive={selectedAccount?.account_type === AccountTypeEnum.STANDARD}
              onClick={() =>
                setSelectedAccount({
                  ...selectedAccount,
                  account_type: AccountTypeEnum.STANDARD,
                } as IAccount)
              }
            >
              Standard
            </AccountButton>
            <AccountButton
              isActive={selectedAccount?.account_type === AccountTypeEnum.MARKET_MAKER}
              onClick={() =>
                setSelectedAccount({
                  ...selectedAccount,
                  account_type: AccountTypeEnum.MARKET_MAKER,
                } as IAccount)
              }
            >
              Market Maker
            </AccountButton>
            <AccountButton
              isActive={selectedAccount?.account_type === AccountTypeEnum.MANAGED_ACCOUNT}
              onClick={() =>
                setSelectedAccount({
                  ...selectedAccount,
                  account_type: AccountTypeEnum.MANAGED_ACCOUNT,
                } as IAccount)
              }
            >
              Managed Account
            </AccountButton>
          </>
        }
        confirmationButton={{
          title: "Update",
          disabled: !selectedAccount,
          onClick: () => updateAccount(selectedAccount as IAccount),
        }}
      />

      <TableWrapper
        style={{
          borderRadius: 0,
          borderTop: "none",
          height: `calc(100dvh - ${COMPONENTS.header.mobile + (dashboardFooterRef?.current?.clientHeight || 0)}px)`,
        }}
      >
        <MobileDirectory />
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeaderCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{ width: column.width }}
                  >
                    {column.render("Header")}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { ...rowProps } = row.getRowProps();
              return (
                // eslint-disable-next-line react/jsx-key
                <DashboardRow
                  {...rowProps}
                  onClick={() => navigate(`/account/${row.original.account}`)}
                  hasRecord={!!row.original.tag}
                >
                  {row.cells.map((cell) => (
                    // eslint-disable-next-line react/jsx-key
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </DashboardRow>
              );
            })}
          </tbody>
        </table>
      </TableWrapper>

      <div ref={dashboardFooterRef}>
        <MobilePaginationCol>
          <div>
            {!isLoading ? (
              <div style={{ color: TEXT_COLORS.two, fontSize: FONT_SIZE.two }}>
                Showing <span style={{ color: TEXT_COLORS.one }}>{displayRange}</span> of{" "}
                <span style={{ color: TEXT_COLORS.one }}>
                  {currency(totalAccounts, { precision: 0 }).format({ symbol: "" })}
                </span>
              </div>
            ) : (
              <Spinner />
            )}
          </div>
          <div>
            <Button
              buttonTheme={ButtonThemeEnum.HIGHLIGHT}
              onClick={onPrevPage}
              type="button"
              disabled={pagination === 1 || isLoading}
            >
              {"<"}
            </Button>
            <Button
              buttonTheme={ButtonThemeEnum.HIGHLIGHT}
              onClick={onNextPage}
              type="button"
              disabled={pagination === lastPage || isLoading}
            >
              {">"}
            </Button>
          </div>
        </MobilePaginationCol>

        <MobileQuickAddressWrapper>
          {Object.entries(quickAddresses).map(([key, value]) => (
            <QuickAddressTag to={`/account/${value}`} key={String(key)}>
              <span>{key}</span>
              <img src={arrow} alt="arrow" />
            </QuickAddressTag>
          ))}
        </MobileQuickAddressWrapper>

        <MobileFooterRow>
          <AccountFilter isMobile />
        </MobileFooterRow>
      </div>
    </>
  );
}
